import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Mode démo si Supabase n'est pas configuré
export const isDemoMode = !supabaseUrl || !supabaseAnonKey;

if (isDemoMode) {
  console.warn('⚠️ Mode DEMO: Supabase non configuré. Créez un fichier .env avec REACT_APP_SUPABASE_URL et REACT_APP_SUPABASE_ANON_KEY');
}

// Créer un client factice si pas de config
export const supabase = isDemoMode
  ? null
  : createClient(supabaseUrl, supabaseAnonKey);

// Helper functions for common operations
export const auth = {
  // Sign up with email and password
  signUp: async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    return { data, error };
  },

  // Sign in with email and password
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Get session
  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Reset password
  resetPassword: async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  },

  // Update password
  updatePassword: async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },
};

// Database helpers
export const db = {
  // Get user profile
  getProfile: async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  // Update user profile
  updateProfile: async (userId, updates) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Get user progress
  getProgress: async (userId) => {
    const { data, error } = await supabase
      .from('user_progress')
      .select('*')
      .eq('user_id', userId)
      .order('module_id', { ascending: true });
    return { data, error };
  },

  // Update module progress
  updateProgress: async (userId, moduleId, progress) => {
    const { data, error } = await supabase
      .from('user_progress')
      .upsert({
        user_id: userId,
        module_id: moduleId,
        progress_percent: progress,
        completed: progress >= 100,
        completed_at: progress >= 100 ? new Date().toISOString() : null,
        last_accessed: new Date().toISOString(),
      }, {
        onConflict: 'user_id,module_id',
      })
      .select()
      .single();
    return { data, error };
  },

  // Mark module as completed
  completeModule: async (userId, moduleId) => {
    return db.updateProgress(userId, moduleId, 100);
  },

  // Get payments history
  getPayments: async (userId) => {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Record payment
  recordPayment: async (userId, paymentData) => {
    const { data, error } = await supabase
      .from('payments')
      .insert({
        user_id: userId,
        ...paymentData,
      })
      .select()
      .single();
    return { data, error };
  },

  // Get certificate
  getCertificate: async (userId) => {
    const { data, error } = await supabase
      .from('certificates')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data, error };
  },

  // Generate certificate
  generateCertificate: async (userId) => {
    const certificateNumber = `F12IA-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    const { data, error } = await supabase
      .from('certificates')
      .insert({
        user_id: userId,
        certificate_number: certificateNumber,
      })
      .select()
      .single();
    return { data, error };
  },

  // Set user as premium (after payment)
  setPremium: async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        is_premium: true,
        has_paid: true,
        payment_date: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },
};

// Candidatures / Leads
export const candidatures = {
  // Capturer un email (lead partiel - depuis Home)
  captureEmail: async (email) => {
    if (isDemoMode) {
      console.log('📧 [DEMO] Email capturé:', email);
      return { data: { id: 'demo-' + Date.now(), email }, error: null };
    }

    const { data, error } = await supabase
      .from('candidatures')
      .upsert({
        email,
        status: 'email_captured',
        source: 'home_inline_form',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'email',
      })
      .select()
      .single();
    return { data, error };
  },

  // Soumettre une candidature complète
  submit: async (formData) => {
    if (isDemoMode) {
      console.log('📝 [DEMO] Candidature soumise:', formData);
      return { data: { id: 'demo-' + Date.now(), ...formData }, error: null };
    }

    const { data, error } = await supabase
      .from('candidatures')
      .upsert({
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        age: formData.age,
        tech_level: formData.techLevel,
        situation: formData.situation,
        objective: formData.objective,
        availability: formData.availability,
        source: formData.source,
        budget: formData.budget,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'email',
      })
      .select()
      .single();
    return { data, error };
  },

  // Récupérer toutes les candidatures (pour admin)
  getAll: async () => {
    if (isDemoMode) return { data: [], error: null };

    const { data, error } = await supabase
      .from('candidatures')
      .select('*')
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Mettre à jour le statut d'une candidature
  updateStatus: async (id, status) => {
    if (isDemoMode) return { data: { id, status }, error: null };

    const { data, error } = await supabase
      .from('candidatures')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },
};

// Email notifications (via Edge Function Resend)
export const notifications = {
  // Envoyer email via Edge Function
  sendEmail: async ({ to, template, data }) => {
    if (isDemoMode) {
      console.log('📧 [DEMO] Email envoyé:', { to, template, data });
      return { success: true, demo: true };
    }

    try {
      const { data: result, error } = await supabase.functions.invoke('send-email', {
        body: { to, template, data },
      });

      if (error) throw error;
      return { success: true, data: result };
    } catch (error) {
      console.error('Erreur envoi email:', error);
      return { success: false, error };
    }
  },

  // Envoyer une notification de nouvelle candidature (au candidat + admin)
  sendCandidatureNotification: async (candidatureData) => {
    if (isDemoMode) {
      console.log('📨 [DEMO] Notifications candidature envoyées pour:', candidatureData.email);
      return { success: true };
    }

    const results = { candidat: null, admin: null };
    const dateStr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const timeStr = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    // Mapping des valeurs pour affichage lisible
    const budgetLabels = {
      'moins-1000': 'Moins de 1 000€',
      '1000-2000': '1 000€ - 2 000€',
      '2000-3000': '2 000€ - 3 000€',
      'plus-3000': 'Plus de 3 000€',
      'facilites': 'Facilités de paiement'
    };
    const situationLabels = {
      'salarie': 'Salarié(e)',
      'freelance': 'Freelance',
      'entrepreneur': 'Entrepreneur(e)',
      'recherche': 'En recherche d\'emploi',
      'etudiant': 'Étudiant(e)',
      'reconversion': 'En reconversion',
      'autre': 'Autre'
    };
    const objectiveLabels = {
      'complement': 'Revenus complémentaires',
      'reconversion': 'Reconversion totale',
      'quitter-job': 'Quitter mon job',
      'freelance': 'Devenir freelance',
      'competences': 'Nouvelles compétences',
      'autre': 'Autre'
    };
    const techLabels = {
      'debutant': 'Débutant',
      'intermediaire': 'Intermédiaire',
      'avance': 'Avancé',
      'expert': 'Expert'
    };
    const availabilityLabels = {
      '5-10': '5-10h/semaine',
      '10-20': '10-20h/semaine',
      '20-30': '20-30h/semaine',
      '30+': '+30h/semaine'
    };

    // 1. Email de confirmation au candidat
    try {
      const candidatTemplate = {
        subject: `${candidatureData.firstName}, ta candidature F12IA est bien reçue`,
        html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #000000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #000000;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width: 600px; width: 100%;">

          <!-- Header -->
          <tr>
            <td align="center" style="padding-bottom: 32px;">
              <table role="presentation" cellspacing="0" cellpadding="0">
                <tr>
                  <td style="background: linear-gradient(135deg, #0066FF 0%, #0052cc 100%); border-radius: 12px; padding: 12px 24px;">
                    <span style="font-size: 24px; font-weight: 800; color: #ffffff; letter-spacing: -0.5px;">F12IA</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Main Card -->
          <tr>
            <td style="background: linear-gradient(180deg, #0a0a0a 0%, #111111 100%); border-radius: 24px; padding: 48px 40px; border: 1px solid #222222;">

              <!-- Success Icon -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="padding-bottom: 24px;">
                    <div style="width: 72px; height: 72px; background: linear-gradient(135deg, rgba(12, 206, 107, 0.2) 0%, rgba(12, 206, 107, 0.05) 100%); border-radius: 50%; border: 2px solid #0cce6b; line-height: 72px; text-align: center;">
                      <span style="font-size: 32px;">✓</span>
                    </div>
                  </td>
                </tr>
              </table>

              <!-- Title -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="padding-bottom: 8px;">
                    <h1 style="margin: 0; font-size: 28px; font-weight: 700; color: #ffffff; letter-spacing: -0.5px;">
                      Candidature reçue !
                    </h1>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding-bottom: 32px;">
                    <p style="margin: 0; font-size: 16px; color: #888888;">
                      ${dateStr}
                    </p>
                  </td>
                </tr>
              </table>

              <!-- Message -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td style="padding-bottom: 32px;">
                    <p style="margin: 0 0 16px 0; font-size: 17px; line-height: 1.7; color: #ededed;">
                      Hey <strong style="color: #ffffff;">${candidatureData.firstName}</strong> 👋
                    </p>
                    <p style="margin: 0 0 16px 0; font-size: 17px; line-height: 1.7; color: #a0a0a0;">
                      Merci pour ta candidature à F12IA ! On l'a bien reçue et on va l'étudier attentivement.
                    </p>
                    <p style="margin: 0; font-size: 17px; line-height: 1.7; color: #a0a0a0;">
                      Notre équipe te recontacte <strong style="color: #ffffff;">sous 48h maximum</strong> par email ou téléphone pour discuter de ton projet.
                    </p>
                  </td>
                </tr>
              </table>

              <!-- Next Steps -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #0a0a0a; border-radius: 16px; border: 1px solid #1a1a1a;">
                <tr>
                  <td style="padding: 24px;">
                    <p style="margin: 0 0 16px 0; font-size: 13px; font-weight: 600; color: #666666; text-transform: uppercase; letter-spacing: 1px;">
                      Prochaines étapes
                    </p>
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="padding: 8px 0;">
                          <table role="presentation" cellspacing="0" cellpadding="0">
                            <tr>
                              <td style="width: 28px; vertical-align: top;">
                                <span style="display: inline-block; width: 20px; height: 20px; background-color: #0066FF; border-radius: 50%; text-align: center; line-height: 20px; font-size: 11px; font-weight: 600; color: #fff;">1</span>
                              </td>
                              <td style="color: #cccccc; font-size: 15px;">Analyse de ta candidature par notre équipe</td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0;">
                          <table role="presentation" cellspacing="0" cellpadding="0">
                            <tr>
                              <td style="width: 28px; vertical-align: top;">
                                <span style="display: inline-block; width: 20px; height: 20px; background-color: #0066FF; border-radius: 50%; text-align: center; line-height: 20px; font-size: 11px; font-weight: 600; color: #fff;">2</span>
                              </td>
                              <td style="color: #cccccc; font-size: 15px;">Appel découverte pour comprendre tes objectifs</td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0;">
                          <table role="presentation" cellspacing="0" cellpadding="0">
                            <tr>
                              <td style="width: 28px; vertical-align: top;">
                                <span style="display: inline-block; width: 20px; height: 20px; background-color: #0cce6b; border-radius: 50%; text-align: center; line-height: 20px; font-size: 11px; font-weight: 600; color: #000;">3</span>
                              </td>
                              <td style="color: #cccccc; font-size: 15px;">Début de ta formation si tu es sélectionné(e)</td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>

              <!-- CTA -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="padding-top: 32px;">
                    <a href="https://f12ia.com" style="display: inline-block; background: linear-gradient(135deg, #0066FF 0%, #0052cc 100%); color: #ffffff; text-decoration: none; font-size: 16px; font-weight: 600; padding: 16px 40px; border-radius: 12px;">
                      Revoir la formation →
                    </a>
                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td align="center" style="padding-top: 32px;">
              <p style="margin: 0 0 8px 0; font-size: 13px; color: #666666;">
                Une question ? Réponds directement à cet email.
              </p>
              <p style="margin: 0; font-size: 12px; color: #444444;">
                © 2025 F12IA · L'IA code. On t'envoie les clients. Tu encaisses.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
        `,
      };

      results.candidat = await notifications.sendEmail({
        to: candidatureData.email,
        template: 'candidature-received',
        data: candidatTemplate,
      });
    } catch (error) {
      console.error('Erreur email candidat:', error);
      results.candidat = { success: false, error };
    }

    // 2. Email de notification aux admins
    try {
      const adminTemplate = {
        subject: `Nouvelle candidature · ${candidatureData.firstName} ${candidatureData.lastName} · ${budgetLabels[candidatureData.budget] || candidatureData.budget}`,
        html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #000000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #000000;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width: 600px; width: 100%;">

          <!-- Header -->
          <tr>
            <td style="padding-bottom: 24px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <span style="font-size: 20px; font-weight: 700; color: #ffffff;">F12IA</span>
                  </td>
                  <td align="right">
                    <span style="display: inline-block; background-color: #0cce6b; color: #000000; font-size: 11px; font-weight: 700; padding: 6px 12px; border-radius: 100px; text-transform: uppercase; letter-spacing: 0.5px;">
                      Nouvelle candidature
                    </span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Main Card -->
          <tr>
            <td style="background-color: #111111; border-radius: 20px; border: 1px solid #222222; overflow: hidden;">

              <!-- Candidate Header -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background: linear-gradient(135deg, #0066FF 0%, #0052cc 100%);">
                <tr>
                  <td style="padding: 32px;">
                    <h1 style="margin: 0 0 4px 0; font-size: 28px; font-weight: 700; color: #ffffff;">
                      ${candidatureData.firstName} ${candidatureData.lastName}
                    </h1>
                    <p style="margin: 0; font-size: 16px; color: rgba(255,255,255,0.8);">
                      ${candidatureData.email}
                    </p>
                  </td>
                </tr>
              </table>

              <!-- Quick Stats -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-bottom: 1px solid #222222;">
                <tr>
                  <td width="33%" align="center" style="padding: 20px; border-right: 1px solid #222222;">
                    <p style="margin: 0 0 4px 0; font-size: 11px; color: #666666; text-transform: uppercase; letter-spacing: 1px;">Budget</p>
                    <p style="margin: 0; font-size: 16px; font-weight: 600; color: #0cce6b;">${budgetLabels[candidatureData.budget] || candidatureData.budget || '-'}</p>
                  </td>
                  <td width="33%" align="center" style="padding: 20px; border-right: 1px solid #222222;">
                    <p style="margin: 0 0 4px 0; font-size: 11px; color: #666666; text-transform: uppercase; letter-spacing: 1px;">Disponibilité</p>
                    <p style="margin: 0; font-size: 16px; font-weight: 600; color: #ffffff;">${availabilityLabels[candidatureData.availability] || candidatureData.availability || '-'}</p>
                  </td>
                  <td width="33%" align="center" style="padding: 20px;">
                    <p style="margin: 0 0 4px 0; font-size: 11px; color: #666666; text-transform: uppercase; letter-spacing: 1px;">Niveau tech</p>
                    <p style="margin: 0; font-size: 16px; font-weight: 600; color: #ffffff;">${techLabels[candidatureData.techLevel] || candidatureData.techLevel || '-'}</p>
                  </td>
                </tr>
              </table>

              <!-- Details -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td style="padding: 24px 32px;">

                    <!-- Contact -->
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom: 24px;">
                      <tr>
                        <td style="padding-bottom: 12px;">
                          <p style="margin: 0; font-size: 12px; font-weight: 600; color: #666666; text-transform: uppercase; letter-spacing: 1px;">Contact</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="background-color: #0a0a0a; border-radius: 12px; padding: 16px;">
                          <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                            <tr>
                              <td width="50%" style="padding: 8px 0;">
                                <span style="color: #666666; font-size: 13px;">Téléphone</span><br>
                                <a href="tel:${candidatureData.phone}" style="color: #ffffff; font-size: 15px; text-decoration: none; font-weight: 500;">${candidatureData.phone || '-'}</a>
                              </td>
                              <td width="50%" style="padding: 8px 0;">
                                <span style="color: #666666; font-size: 13px;">Âge</span><br>
                                <span style="color: #ffffff; font-size: 15px; font-weight: 500;">${candidatureData.age || '-'}</span>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>

                    <!-- Profil -->
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom: 24px;">
                      <tr>
                        <td style="padding-bottom: 12px;">
                          <p style="margin: 0; font-size: 12px; font-weight: 600; color: #666666; text-transform: uppercase; letter-spacing: 1px;">Profil</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="background-color: #0a0a0a; border-radius: 12px; padding: 16px;">
                          <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                            <tr>
                              <td style="padding: 8px 0; border-bottom: 1px solid #1a1a1a;">
                                <span style="color: #666666; font-size: 13px;">Situation actuelle</span><br>
                                <span style="color: #ffffff; font-size: 15px; font-weight: 500;">${situationLabels[candidatureData.situation] || candidatureData.situation || '-'}</span>
                              </td>
                            </tr>
                            <tr>
                              <td style="padding: 8px 0;">
                                <span style="color: #666666; font-size: 13px;">Objectif principal</span><br>
                                <span style="color: #ffffff; font-size: 15px; font-weight: 500;">${objectiveLabels[candidatureData.objective] || candidatureData.objective || '-'}</span>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>

                    <!-- Source -->
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="background-color: #0a0a0a; border-radius: 12px; padding: 16px;">
                          <span style="color: #666666; font-size: 13px;">Source d'acquisition</span><br>
                          <span style="color: #0066FF; font-size: 15px; font-weight: 500;">${candidatureData.source || '-'}</span>
                        </td>
                      </tr>
                    </table>

                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td align="center" style="padding-top: 24px;">
              <p style="margin: 0; font-size: 13px; color: #444444;">
                Reçu le ${dateStr} à ${timeStr}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
        `,
      };

      results.admin = await notifications.sendEmail({
        to: 'admin',
        template: 'admin-new-candidature',
        data: adminTemplate,
      });
    } catch (error) {
      console.error('Erreur email admin:', error);
      results.admin = { success: false, error };
    }

    return {
      success: results.candidat?.success || results.admin?.success,
      results,
    };
  },

  // Notifier les admins d'un email capturé (lead partiel)
  notifyEmailCaptured: async (email) => {
    if (isDemoMode) {
      console.log('📧 [DEMO] Admin notifié - email capturé:', email);
      return { success: true };
    }

    const dateStr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const timeStr = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const template = {
      subject: `Lead capturé · ${email}`,
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #000000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #000000;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width: 600px; width: 100%;">

          <!-- Header -->
          <tr>
            <td style="padding-bottom: 24px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <span style="font-size: 20px; font-weight: 700; color: #ffffff;">F12IA</span>
                  </td>
                  <td align="right">
                    <span style="display: inline-block; background-color: #0066FF; color: #ffffff; font-size: 11px; font-weight: 700; padding: 6px 12px; border-radius: 100px; text-transform: uppercase; letter-spacing: 0.5px;">
                      Lead capturé
                    </span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Main Card -->
          <tr>
            <td style="background-color: #111111; border-radius: 20px; border: 1px solid #222222; padding: 32px;">

              <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="padding-bottom: 24px;">
                    <div style="width: 56px; height: 56px; background: linear-gradient(135deg, rgba(0, 102, 255, 0.2) 0%, rgba(0, 102, 255, 0.05) 100%); border-radius: 50%; border: 2px solid #0066FF; line-height: 56px; text-align: center;">
                      <span style="font-size: 24px;">📧</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td align="center">
                    <p style="margin: 0 0 8px 0; font-size: 14px; color: #666666;">Nouvel email capturé</p>
                    <p style="margin: 0 0 24px 0; font-size: 22px; font-weight: 600; color: #ffffff;">${email}</p>
                    <table role="presentation" cellspacing="0" cellpadding="0" style="background-color: #0a0a0a; border-radius: 8px;">
                      <tr>
                        <td style="padding: 12px 20px;">
                          <span style="color: #666666; font-size: 13px;">Source : </span>
                          <span style="color: #ffffff; font-size: 13px; font-weight: 500;">Formulaire Home (CTA final)</span>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td align="center" style="padding-top: 24px;">
              <p style="margin: 0; font-size: 13px; color: #444444;">
                ${dateStr} à ${timeStr}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
      `,
    };

    return notifications.sendEmail({
      to: 'admin',
      template: 'email-captured',
      data: template,
    });
  },
};

export default supabase;
